import { useMemo } from "react";

import {
  useAudiencesAndMetricsForParentModelQuery,
  useParentModelAndRelationshipsQuery,
  useParentModelDecisionEngineFlowsQuery,
  useParentModelsForAnalyticsQuery,
  useSubsetsForParentModelQuery,
  useTraitsForParentModelQuery,
} from "src/graphql";

import { useChartConfigurePermission } from "src/components/permission/use-chart-configure-permission";
import { useUser } from "src/contexts/user-context";
import { DecisionEngineFlowIcon } from "src/pages/analytics/common";
import type { DecisionEngineFlow } from "src/pages/analytics/decision-engine-utils";
import {
  type MeasurementGroupOption,
  MeasurementScope,
  type ParentModelOption,
} from "src/pages/analytics/types";
import { transformAudiencesAndMetrics } from "./utils";

const formatAvailableMeasurements = (
  parentModelId: number | undefined,
  decisionEngineFlows: DecisionEngineFlow[] | undefined,
): MeasurementGroupOption[] => {
  if (!parentModelId) {
    return [];
  }

  // Supported resource types: AID Flows
  const groupOptions: MeasurementGroupOption[] = [];

  const flows = (decisionEngineFlows ?? [])
    // Only want ready decision engine flows
    .filter((flow) => flow.status === "ready")
    .map((flow) => ({
      id: flow.id,
      label: flow.name,
      scope: MeasurementScope.DecisionEngineFlow,
      icon: DecisionEngineFlowIcon,
    }));

  if (flows.length > 0) {
    groupOptions.push({
      groupLabel: "AI Decisioning Agents",
      options: flows,
    });
  }

  return groupOptions;
};

type UseChartDependencies = {
  parentModelId: number | undefined;
};

/**
 * Hook for fetching the schema information for charts
 */
export const useChartDependencies = ({
  parentModelId,
}: UseChartDependencies) => {
  const { modelFilter: isModelPreviewable } = useChartConfigurePermission();

  const parentModelsForAnalyticsQuery = useParentModelsForAnalyticsQuery(
    undefined,
    {
      refetchOnMount: "always",
      select: (data) => data.segments,
    },
  );

  const parentModelOptions = useMemo<ParentModelOption[]>(
    () =>
      parentModelsForAnalyticsQuery.data?.map((option) => ({
        value: option.id,
        label: option.name,
        connection: option.connection,
        disabled: !isModelPreviewable(option.id),
      })) ?? [],
    [parentModelsForAnalyticsQuery.data, isModelPreviewable],
  );

  const parentModelAndRelationships = useParentModelAndRelationshipsQuery(
    { id: parentModelId?.toString() ?? "" },
    { enabled: Boolean(parentModelId) },
  );

  const audiencesAndMetrics = useAudiencesAndMetricsForParentModelQuery(
    { id: parentModelId?.toString() ?? "" },
    {
      enabled: Boolean(parentModelId),
      select: (data) => transformAudiencesAndMetrics(data),
      keepPreviousData: true,
    },
  );

  const parentModel = parentModelAndRelationships.data?.segments_by_pk ?? null;

  const events = useMemo(
    () =>
      parentModel?.relationships?.filter(({ to_model: { event } }) =>
        Boolean(event),
      ) ?? [],
    [parentModel?.relationships],
  );

  // NOTE: We include merged models here, just like in the audience builder
  const relatedModels = useMemo(
    () =>
      parentModel?.relationships?.filter(({ to_model }) => !to_model.event) ??
      [],
    [parentModel?.relationships],
  );

  const { data: traits, isLoading: isTraitsLoading } =
    useTraitsForParentModelQuery(
      {
        segmentId: parentModelId?.toString() ?? "",
      },
      {
        enabled: Boolean(parentModelId),
        select: (data) => data.trait_definitions,
      },
    );

  const { data: decisionEngineFlows, isLoading: isMeasurementsLoading } =
    useParentModelDecisionEngineFlowsQuery(
      { parentModelId: parentModelId?.toString() ?? "" },
      {
        enabled: Boolean(parentModelId),
        select: (data) => data.decision_engine_flows,
      },
    );

  const measurementOptions = formatAvailableMeasurements(
    parentModelId,
    decisionEngineFlows,
  );

  const { isLoading: isUserLoading } = useUser();

  const { data: subsetCategoryOptions = [], isLoading: isSubsetsLoading } =
    useSubsetsForParentModelQuery(
      {
        parentModelId: String(parentModelId),
      },
      {
        enabled: Boolean(!isUserLoading && parentModelId),
        select: (data) =>
          data.audience_subset_groups.map((subset_group) => ({
            ...subset_group,
            is_required: subset_group.required_models.some(
              (required_model) =>
                String(required_model.segment_id) === String(parentModelId),
            ),
          })),
      },
    );

  const dependenciesLoading =
    parentModelsForAnalyticsQuery.isLoading ||
    parentModelAndRelationships.isLoading ||
    audiencesAndMetrics.isLoading ||
    isTraitsLoading ||
    isMeasurementsLoading ||
    isSubsetsLoading;

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const values = useMemo(
    () => ({
      events,
      relatedModels,
      traits: traits ?? [],
      parentModel,
      parentModelId,
      parentModelOptions,
      audienceOptions: audiencesAndMetrics.data?.segments ?? [],
      metricOptions: audiencesAndMetrics.data?.goals ?? [],
      parentModelLoading: parentModelAndRelationships.isLoading,
      parentModelOptionsLoading: parentModelsForAnalyticsQuery.isLoading,
      measurementOptions: measurementOptions ?? [],
      subsetCategoryOptions: subsetCategoryOptions ?? [],
      dependenciesLoading,
      measurementsOptionsLoading: isMeasurementsLoading,
    }),
    [
      audiencesAndMetrics.data,
      parentModelAndRelationships.isLoading,
      parentModelsForAnalyticsQuery.isLoading,
      events,
      relatedModels,
      subsetCategoryOptions,
      traits,
      parentModel,
      parentModelId,
      parentModelOptions,
      isMeasurementsLoading,
      decisionEngineFlows, // Explicitly added to reset value
      dependenciesLoading,
      measurementOptions,
    ],
  );

  return values;
};
